import { Alert } from "@mui/material";
import * as Sentry from "@sentry/react";
import { createFileRoute, createLink, Navigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog/Dialog";
import { Drawer } from "@/components/Drawer/Drawer";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { ErrorPanel } from "@/components/Errors/ErrorPanel";
import { IconSadFace } from "@/components/Icons";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { PaymentChooser } from "@/components/PaymentChooser/PaymentChooser";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { HotelRoomSelection } from "@/features/hotels/components/RoomSelection/HotelRoomSelection";
import { useHotelBookingContext } from "@/features/hotels/contexts/HotelBookingContext";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelOffersContext } from "@/features/hotels/contexts/HotelOffersContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { useHotelRoomAvailabilityMutation } from "@/features/hotels/hooks/useHotelRoomAvailability";
import { useHotelSearch } from "@/features/hotels/hooks/useHotelSearch";
import type { HotelBooking } from "@/features/hotels/types/types";
import { calculateBookingPrice } from "@/features/hotels/utils/calculateBookingPrice";
import { getNumberOfBookingNights } from "@/features/hotels/utils/getNumberOfBookingNights";
import {
  BnplTrackingEvents,
  HotelTrackingEvents,
  rudderAnalytics,
} from "@/lib/rudderstack";
import type { GetHotelAvailabilityTokenOutputData } from "@/types/codegen";
import { isOfferIsNotAvailableAnymoreError } from "@/utils/assertions";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/rooms",
)({
  component: RouteComponent,
  staticData: {
    hotelStep: { stepNumber: 1 },
    drawer: true,
  },
});

const ButtonLink = createLink(Button);

function RouteComponent() {
  const t = useTranslations();
  const navigate = Route.useNavigate();
  const { id } = Route.useParams();
  const params = Route.useParams();

  const { data: offers } = useHotelOffersContext();
  const { data: hotelData } = useHotelDataContext();
  const { searchResultKey, searchResultToken } = useHotelSearch();
  const {
    data: booking,
    setData: setHotelBooking,
    setAvailabilityToken,
  } = useHotelBookingContext();
  const { searchData } = useHotelSearchDataContext();

  const [showPaymentChooser, setShowPaymentChooser] = useState(false);
  const { paymentType, isEligible } = usePaymentContext();

  const isBnplFeatureEnabled = useFeatureIsOn("bnpl") && Boolean(isEligible);

  const [paymentError, setPaymentError] = useState<string | null>(null);

  const totalPrice = booking ? calculateBookingPrice(booking) : null;
  const fallbackPrice = hotelData?.minPrice?.value || 0;
  const originalPrice = totalPrice?.value || fallbackPrice;

  const {
    mutateAsync: getRoomAvailability,
    isPending: isRoomAvailabilityPending,
    error: roomAvailabilityError,
  } = useHotelRoomAvailabilityMutation({
    onSuccess: (data: GetHotelAvailabilityTokenOutputData) => {
      setAvailabilityToken(data.availabilityToken);
    },
  });

  const handleContinue = async (booking: HotelBooking) => {
    setPaymentError(null);

    const eventName =
      paymentType === "bnpl"
        ? BnplTrackingEvents.USER_SELECTED_BNPL_PAYMENT
        : BnplTrackingEvents.USER_SELECTED_STANDARD_PAYMENT;

    rudderAnalytics.track(eventName, {
      is_bnpl_eligible: isEligible,
    });

    const currentOffer = offers?.find(
      (offer) =>
        offer.offerId === booking.offerId &&
        offer.packageToken === booking.packageToken,
    );

    if (!currentOffer || !searchResultKey || !searchResultToken) {
      setPaymentError("Cannot check room availability, params missing");
      return;
    }

    const totalPrice = calculateBookingPrice(booking);

    await getRoomAvailability({
      hotelId: id,
      searchResultKey,
      searchResultToken,
      packageToken: currentOffer.packageToken,
      offerId: booking.offerId,
      roomTokens: booking.rooms.map((room) => room.roomToken),
    });

    rudderAnalytics.track(
      HotelTrackingEvents.USER_PROCEED_AFTER_ROOM_SELECTION,
      {
        bookingType: "Hotel",
        selected_offer_id: booking?.offerId,
        price: totalPrice.value,
      },
    );
    navigate({ to: "/hotels/$id/travelers" });
  };

  if (!searchData || !hotelData) {
    Sentry.captureMessage(
      "User tried to access hotel rooms page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/$id/details" params={params} />;
  }

  const bookingNights = getNumberOfBookingNights({
    checkIn: searchData.dates.checkIn,
    checkOut: searchData.dates.checkOut,
  });

  const guestCount = searchData.rooms.reduce(
    (total, room) => total + room.adults + room.children,
    0,
  );

  return (
    <>
      <div className="w-full px-4">
        <div className="mb-5 text-lg font-medium">
          {t("hotels.availableRoomOptions")}
        </div>

        {offers.map((offer) => (
          <HotelRoomSelection
            key={offer.offerId}
            hotel={hotelData}
            rooms={offer.rooms}
            guestCount={guestCount}
            bookingNights={bookingNights}
            onRoomSelected={(selectedRooms) => {
              const bookingData = {
                packageToken: offer.packageToken,
                rooms: selectedRooms,
                offerId: offer.offerId,
              };
              setHotelBooking(bookingData);

              if (isBnplFeatureEnabled) {
                setShowPaymentChooser(true);
              } else {
                handleContinue(bookingData);
              }
            }}
            onRoomGalleryClick={(option) => {
              navigate({
                to: "./$roomToken/images",
                params: { roomToken: option.roomToken },
              });
            }}
          />
        ))}
      </div>

      <Dialog
        isOpen={
          isRoomAvailabilityPending ||
          isOfferIsNotAvailableAnymoreError(roomAvailabilityError)
        }
      >
        {isRoomAvailabilityPending ? (
          <PageLoader>
            <AnimatedLoader icon="buildings" />
            <h3 className="mt-3 font-normal text-text-primary">
              {t("hotels.checkingRoomAvailability")}
            </h3>
          </PageLoader>
        ) : (
          <ErrorPanel
            icon={IconSadFace}
            primaryText={t("hotels.notAvailable.title")}
            secondaryText={t("hotels.notAvailable.description")}
          >
            <ButtonLink
              variant="outlined"
              borderRoundness="medium"
              color="primary"
              size="small"
              to="/hotels/list"
            >
              {t("hotels.notAvailable.button")}
            </ButtonLink>
          </ErrorPanel>
        )}
      </Dialog>

      <Drawer
        open={showPaymentChooser}
        onClose={() => setShowPaymentChooser(false)}
      >
        <DrawerTopBar
          onCloseClick={() => setShowPaymentChooser(false)}
          title={t("payment.paymentTypeHeader")}
        />

        <PaymentChooser price={originalPrice} />
        {paymentError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {paymentError}
          </Alert>
        )}

        <Button
          fullWidth
          disabled={
            !booking || searchData?.rooms.length !== booking?.rooms.length
          }
          variant="contained"
          onClick={() => handleContinue(booking!)}
        >
          {t("hotels.search.bookNow")}
        </Button>
      </Drawer>
    </>
  );
}
